import React, { Component } from 'react';
import styleable from 'react-styleable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { DatePicker, DataTable } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';
import HeaderToggle from '../../../components/participation-table/header-toggle/header-toggle';

import css from './single-class.scss';

import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';
import singleClassPropTypes from './single-class-prop-types';
import {
    SELECTED_DATE,
    SELECTED_STUDENT,
    SELECTED_GROUP,
    SELECTED_CLASS,
    ALL_GROUPS,
    SELECTED_YEAR_GROUP,
    SELECTED_TEACHER
} from '../../../components/report-filters/filter-constants';
import { SINGLE_CLASS_USAGE_TABLE_CONFIG } from './single-class-constants';
import {
    getSingleClassParticipationTableConfig,
    GROUP_NAME,
    STUDENT_NAME,
    NEW_CERTIFICATES_TOTAL,
    PAST_CERTIFICATES_TOTAL
} from './single-class-constants-update';
import certificatesActions from '../../../components/certificates/certificates-actions';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';
import singleClassActions, {
    PARTICIPATION_CLASS_RETRIEVED
} from './single-class-actions';

import FilterHelper from '../../../helpers/filter-helper';

import TableHeader from '../../../components/table-header/table-header';
import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';
import SingleClassCertificates from './components/single-class-certificates';
import Usage from '../../../components/usage/usage';
import { ALL_GROUPS_ID } from '../all-classes/all-classes-constants';
/**
 * SingleClass component
 * Displays participation results table, certificates & usage for a single class or group
 */
export class SingleClass extends Component {
    static propTypes = {
        participationData: singleClassPropTypes.participationData,
        activeFilters: activeFiltersPropType,
        getParticipationData: PropTypes.func,
        updateActiveFilter: PropTypes.func,
        invalidRequest: PropTypes.string,
        newCertificatesDownloadByType: PropTypes.func,
        pastCertificatesDownloadByType: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.state = {
            participationData: null,
            participationTableConfig: null,
            dataTable: {
                isPastCertificatesExpanded: false,
                isNewCertificatesExpanded: false
            }
        };
        this.i18n = window.i18n;
        this.text = {
            activityCourseText: `${this.i18n._('Label - ActivityCourse')}:`,
            skillQuestsText: `${this.i18n._('Time - Skill Quests')}:`,
            skillQuestsEmptyText: this.i18n._(
                'Table Empty - Skill Quests course not set'
            ),
            groups: this.i18n._('Table Config - Groups')
        };
    }

    componentDidMount() {
        const { activeFilters } = this.props;
        if (FilterHelper.shouldLoadSingleClass(activeFilters)) {
            this.getClassParticipationData(
                activeFilters[SELECTED_DATE],
                activeFilters[SELECTED_CLASS].id
            );
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.dataTable.isPastCertificatesExpanded !==
                this.state.dataTable.isPastCertificatesExpanded ||
            prevState.dataTable.isNewCertificatesExpanded !==
                this.state.dataTable.isNewCertificatesExpanded
        ) {
            this.setParticipationTableConfig(
                this.props.participationData,
                this.props.activeFilters[SELECTED_GROUP].id
            );
        }
    }

    componentWillReceiveProps(newProps) {
        const {
            activeFilters,
            participationData,
            invalidRequest,
            updateActiveFilter
        } = this.props;
        const {
            activeFilters: newActiveFilters,
            participationData: newParticipationData,
            invalidRequest: newInvalidRequest
        } = newProps;

        if (
            FilterHelper.shouldReloadSingleClass(
                activeFilters,
                newActiveFilters
            )
        ) {
            this.getClassParticipationData(
                newActiveFilters[SELECTED_DATE],
                newActiveFilters[SELECTED_CLASS].id
            );
        }

        if (participationData !== newParticipationData) {
            this.setParticipationData(
                newParticipationData,
                newActiveFilters[SELECTED_GROUP].id
            );
            this.setUsageTableConfig();
        }

        if (
            invalidRequest !== newInvalidRequest &&
            newInvalidRequest === PARTICIPATION_CLASS_RETRIEVED
        ) {
            if (
                newActiveFilters[SELECTED_DATE].type !==
                DatePicker.THIS_SCHOOL_YEAR
            ) {
                updateActiveFilter(SELECTED_DATE, {
                    type: DatePicker.THIS_SCHOOL_YEAR
                });
            }
        }
    }

    getClassParticipationData(selectedDate, classId) {
        this.props.getParticipationData(classId, selectedDate);
    }

    setParticipationData(data, groupId) {
        groupId = +groupId || 0;
        const participationData = {
            ...data
        };
        if (groupId) {
            const groupData = participationData.classroomGroupParticipations.find(
                (group) => group.id === +groupId
            );
            if (groupData) {
                participationData.classroomGroupParticipations = [groupData];
                participationData.certificateDetails =
                    groupData.certificateDetails;
                participationData.usage = groupData.usage;
            } else {
                const {
                    classroomGroupParticipations,
                    certificateDetails,
                    usage
                } = this.getDefaultParticipationData();
                participationData.classroomGroupParticipations = classroomGroupParticipations;
                participationData.certificateDetails = certificateDetails;
                participationData.usage = usage;
            }
        }

        this.setParticipationTableConfig(participationData, groupId);
        this.setState({
            participationData
        });
    }

    getDefaultParticipationData = () => ({
        classroomGroupParticipations: [],
        certificateDetails: { gold: 0, silver: 0, bronze: 0 },
        usage: [
            {
                id: 0,
                activityPoints: 0,
                livePoints: 0,
                totalPoints: 0,
                signIns: 0,
                timeOnline: { hours: 0, minutes: 0 }
            }
        ]
    });

    updateParticipationTableConfig(participationTableConfig, groupId) {
        // Update the group name
        participationTableConfig.colGroups[0].find(
            (col) => col.id === GROUP_NAME
        ).name = this.text.groups;
        // If page is displays all groups OR no group is selected
        if (!groupId) {
            // Set group click event
            participationTableConfig.cols.find(
                (col) => col.id === GROUP_NAME
            ).onClick = (selectedGroup) => {
                this.loadNewPage(SELECTED_GROUP, selectedGroup);
            };
            // Set group sort
            participationTableConfig.colGroups[0].find(
                (col) => col.id === GROUP_NAME
            ).sort = {
                colId: GROUP_NAME,
                sortAs: 'string',
                sortDir: 'desc'
            };
        }
        // Lokalise
        participationTableConfig.colGroups[0].map(
            (col) => (col.name = window.i18n._(col.name))
        );
        participationTableConfig.cols.map(
            (col) => (col.name = window.i18n._(col.name))
        );
        // Set student click event
        participationTableConfig.nestedData.cols.find(
            (col) => col.id === STUDENT_NAME
        ).onClick = (selectedStudent) => {
            this.loadNewPage(SELECTED_STUDENT, selectedStudent);
        };

        participationTableConfig.colGroups[0].map((item) => {
            if (item.id === PAST_CERTIFICATES_TOTAL) {
                item.headerTransform = () => (
                    <HeaderToggle
                        title={item.name}
                        onClick={(value) =>
                            this.setState({
                                dataTable: {
                                    ...this.state.dataTable,
                                    isPastCertificatesExpanded: value
                                }
                            })
                        }
                        defaultExpanded={this.state.isPastCertificatesExpanded}
                    />
                );
            }
            if (item.id === NEW_CERTIFICATES_TOTAL) {
                item.headerTransform = () => (
                    <HeaderToggle
                        title={item.name}
                        onClick={(value) =>
                            this.setState({
                                dataTable: {
                                    ...this.state.dataTable,
                                    isNewCertificatesExpanded: value
                                }
                            })
                        }
                        defaultExpanded={this.state.isNewCertificatesExpanded}
                    />
                );
            }

            return item;
        });

        return participationTableConfig;
    }

    setParticipationTableConfig(participationData, groupId) {
        const { activeFilters } = this.props;
        const classroomId = activeFilters[SELECTED_CLASS].id;
        let participationTableConfig = getSingleClassParticipationTableConfig({
            ...this.state.dataTable,
            classroomId
        });

        participationTableConfig = this.updateParticipationTableConfig(
            participationTableConfig,
            groupId
        );

        this.setState({
            participationTableConfig: cloneDeep(participationTableConfig)
        });
    }

    /**
     * Loads the group or student page when user clicks on item in table
     */
    loadNewPage = (filterName, selectedItem) => {
        this.props.updateActiveFilter(filterName, {
            id: selectedItem.id,
            name: selectedItem.name
        });
    };

    setUsageTableConfig = () => {
        const singleClassUsageTableConfig = cloneDeep(
            SINGLE_CLASS_USAGE_TABLE_CONFIG
        );
        singleClassUsageTableConfig.cols.map(
            (col) => (col.name = window.i18n._(col.name))
        );
        this.setState({ singleClassUsageTableConfig });
    };

    getUsageData = (participationData) => {
        const usageData = participationData && participationData.usage[0];
        if (usageData) {
            const {
                signIns,
                timeOnline,
                livePoints,
                activityPoints
            } = usageData;
            return {
                totalSignIns: signIns,
                totalTimeOnline: timeOnline,
                totalLiveMathsPoints: livePoints,
                totalCurriculumPoints: activityPoints
            };
        }

        return null;
    };

    render() {
        const { participationData, participationTableConfig } = this.state;
        const {
            activeFilters,
            newCertificatesDownloadByType,
            pastCertificatesDownloadByType
        } = this.props;
        const selectedGroupId = activeFilters[SELECTED_GROUP].id;
        const participationHeader = this.i18n._('Table Header - Participation');

        return (
            <div className={css.component}>
                <div className={css['participation-results']}>
                    <TableHeader title={participationHeader} />
                    <Usage {...this.getUsageData(participationData)} />
                    <SingleClassCertificates
                        selectedClassroomId={activeFilters[SELECTED_CLASS].id}
                        selectedGroupId={
                            selectedGroupId === ALL_GROUPS
                                ? ALL_GROUPS_ID
                                : selectedGroupId
                        }
                        participationData={participationData}
                        newCertificatesDownloadByType={
                            newCertificatesDownloadByType
                        }
                        pastCertificatesDownloadByType={
                            pastCertificatesDownloadByType
                        }
                        selectedYearGroupId={
                            activeFilters[SELECTED_YEAR_GROUP].id
                        }
                        selectedTeacherId={activeFilters[SELECTED_TEACHER].id}
                    />
                    <DataTable
                        config={participationTableConfig}
                        data={
                            participationData &&
                            participationData.classroomGroupParticipations
                        }
                    />
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        participationData: state.participationSingleClass,
        invalidRequest: state.invalidRequest
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            getParticipationData: singleClassActions.getParticipationData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter,
            newCertificatesDownloadByType:
                certificatesActions.newCertificatesDownloadByType,
            pastCertificatesDownloadByType:
                certificatesActions.pastCertificatesDownloadByType
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(SingleClass));



// WEBPACK FOOTER //
// ./containers/participation/single-class/single-class.js